import React, { useState, useEffect, useCallback } from "react";
import {
  Shield, RefreshCw, Trash2, ArrowRight, Search, Bot,
  AlertTriangle, Check, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import api from "../api/client";

const STATUS_COLORS = {
  unresolved: "text-red-400 bg-red-900/20",
  redirected: "text-green-400 bg-green-900/20",
  ignored: "text-gray-400 bg-gray-800/40",
};

export default function FourOhFourManager({ selectedSite }) {
  const siteId = selectedSite?.id;
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [selected, setSelected] = useState(new Set());

  // Redirect modal
  const [redirectModal, setRedirectModal] = useState(null);
  const [redirectTo, setRedirectTo] = useState("");

  const fetchStats = useCallback(() => {
    if (!siteId) return;
    api.get(`/websites/${siteId}/bridge/404s/stats/`).then((r) => setStats(r.data)).catch(() => {});
  }, [siteId]);

  const fetchEntries = useCallback(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/bridge/404s/`, {
      params: { page, per_page: perPage, status: statusFilter },
    })
      .then((r) => {
        setEntries(r.data.data || []);
        setTotal(r.data.total || 0);
        setSelected(new Set());
      })
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [siteId, page, perPage, statusFilter]);

  useEffect(() => { fetchStats(); fetchEntries(); }, [fetchStats, fetchEntries]);
  useEffect(() => { setPage(1); }, [statusFilter]);

  const showMsg = (text, type = "success") => {
    setActionMsg({ text, type });
    setTimeout(() => setActionMsg(null), 4000);
  };

  const totalPages = Math.ceil(total / perPage);

  const handleBulkIgnoreBots = () => {
    api.post(`/websites/${siteId}/bridge/404s/bulk-ignore-bots/`).then((r) => {
      showMsg(`Found ${r.data.found} bot probes, ignored ${r.data.ignored}`);
      fetchStats();
      fetchEntries();
    }).catch(() => showMsg("Failed", "error"));
  };

  const handlePurge = () => {
    if (!window.confirm("Purge all 404 entries older than 90 days?")) return;
    api.post(`/websites/${siteId}/bridge/404s/purge/`, { days: 90 }).then((r) => {
      showMsg(`Purged ${r.data.purged} old entries`);
      fetchStats();
      fetchEntries();
    }).catch(() => showMsg("Purge failed", "error"));
  };

  const handleIgnore = (id) => {
    api.post(`/websites/${siteId}/bridge/404s/${id}/`, { action: "ignore" }).then(() => {
      showMsg("Entry ignored");
      fetchStats();
      fetchEntries();
    });
  };

  const handleDelete = (id) => {
    api.delete(`/websites/${siteId}/bridge/404s/${id}/`).then(() => {
      showMsg("Entry deleted");
      fetchStats();
      fetchEntries();
    });
  };

  const handleResolve = () => {
    if (!redirectTo.trim()) return;
    api.post(`/websites/${siteId}/bridge/404s/${redirectModal.id}/`, {
      action: "resolve", redirect_to: redirectTo.trim(),
    }).then(() => {
      showMsg("Redirect created");
      setRedirectModal(null);
      setRedirectTo("");
      fetchStats();
      fetchEntries();
    }).catch(() => showMsg("Failed to create redirect", "error"));
  };

  const handleBulkAction = (action) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    if (action === "delete" && !window.confirm(`Delete ${ids.length} entries?`)) return;
    Promise.all(
      ids.map((id) =>
        action === "delete"
          ? api.delete(`/websites/${siteId}/bridge/404s/${id}/`)
          : api.post(`/websites/${siteId}/bridge/404s/${id}/`, { action: "ignore" })
      )
    ).then(() => {
      showMsg(`${ids.length} entries ${action === "delete" ? "deleted" : "ignored"}`);
      setSelected(new Set());
      fetchStats();
      fetchEntries();
    });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === entries.length) setSelected(new Set());
    else setSelected(new Set(entries.map((e) => e.id)));
  };

  const filteredEntries = entries.filter((e) => {
    if (!search) return true;
    return e.url?.toLowerCase().includes(search.toLowerCase());
  });

  if (!selectedSite) return <div className="text-gray-500 text-center py-20">Select a website</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Shield size={20} className="text-copper" /> 404 Manager
        </h2>
        {actionMsg && (
          <span className={`text-xs px-3 py-1 rounded-lg ${actionMsg.type === "error" ? "bg-red-900/20 text-red-400" : "bg-green-900/20 text-green-400"}`}>
            {actionMsg.text}
          </span>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Unresolved", value: stats.unresolved, color: "text-red-400" },
            { label: "Redirected", value: stats.redirected, color: "text-green-400" },
            { label: "Ignored", value: stats.ignored, color: "text-gray-400" },
            { label: "This Month", value: stats.this_month, color: "text-blue-400" },
          ].map((s) => (
            <div key={s.label} className="bg-dark-300 border border-dark-50 rounded-xl p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value ?? 0}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={handleBulkIgnoreBots} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-900/20 border border-amber-800/30 rounded-lg text-amber-400 hover:bg-amber-900/30 font-medium">
          <Bot size={12} /> Ignore Bot Probes
        </button>
        <button onClick={handlePurge} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-400 hover:text-white">
          <Trash2 size={12} /> Purge Old
        </button>
        <button onClick={() => { fetchStats(); fetchEntries(); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-400 hover:text-white">
          <RefreshCw size={12} /> Refresh
        </button>

        {selected.size > 0 && (
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-dark-50">
            <span className="text-xs text-gray-400">{selected.size} selected</span>
            <button onClick={() => handleBulkAction("ignore")} className="px-2 py-1 text-xs bg-gray-800 rounded text-gray-300 hover:text-white">Ignore</button>
            <button onClick={() => handleBulkAction("delete")} className="px-2 py-1 text-xs bg-red-900/30 rounded text-red-400 hover:text-red-300">Delete</button>
          </div>
        )}
      </div>

      {/* Filter Tabs + Search */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {["all", "unresolved", "redirected", "ignored"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                statusFilter === s ? "bg-copper/20 text-copper border border-copper/30" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {stats && s !== "all" && ` (${stats[s] ?? 0})`}
              {stats && s === "all" && ` (${stats.total ?? 0})`}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search URLs..."
            className="bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-3 py-1.5 text-xs w-48 focus:outline-none focus:border-copper/50"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-dark-50">
                <th className="py-2.5 px-3 w-8">
                  <input type="checkbox" checked={selected.size === entries.length && entries.length > 0} onChange={toggleSelectAll} className="rounded bg-dark border-dark-50 text-copper focus:ring-0" />
                </th>
                <th className="text-left py-2.5 px-3 font-medium">URL</th>
                <th className="text-left py-2.5 px-3 font-medium w-16">Hits</th>
                <th className="text-left py-2.5 px-3 font-medium w-24">Status</th>
                <th className="text-left py-2.5 px-3 font-medium w-28">Last Hit</th>
                <th className="text-left py-2.5 px-3 font-medium w-40">Redirect</th>
                <th className="py-2.5 px-3 font-medium w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
              ) : filteredEntries.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">No 404 entries found</td></tr>
              ) : (
                filteredEntries.map((e) => (
                  <tr key={e.id} className={`border-b border-dark-50/50 hover:bg-dark-50/30 ${selected.has(e.id) ? "bg-copper/5" : ""}`}>
                    <td className="py-2 px-3">
                      <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSelect(e.id)} className="rounded bg-dark border-dark-50 text-copper focus:ring-0" />
                    </td>
                    <td className="py-2 px-3">
                      <code className="text-xs text-gray-300 break-all">{e.url}</code>
                    </td>
                    <td className="py-2 px-3 font-bold text-white">{e.hits}</td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${STATUS_COLORS[e.status] || "text-gray-400"}`}>
                        {e.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-500">{e.last_hit ? new Date(e.last_hit).toLocaleDateString() : "-"}</td>
                    <td className="py-2 px-3 text-xs text-gray-500 truncate max-w-[160px]">{e.redirect_to || "-"}</td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1">
                        {e.status === "unresolved" && (
                          <>
                            <button onClick={() => { setRedirectModal(e); setRedirectTo(""); }} className="px-2 py-1 text-[10px] bg-copper/20 text-copper rounded hover:bg-copper/30" title="Add Redirect">
                              <ArrowRight size={10} />
                            </button>
                            <button onClick={() => handleIgnore(e.id)} className="px-2 py-1 text-[10px] bg-gray-800 text-gray-400 rounded hover:text-white" title="Ignore">
                              <X size={10} />
                            </button>
                          </>
                        )}
                        <button onClick={() => handleDelete(e.id)} className="px-2 py-1 text-[10px] bg-red-900/20 text-red-400 rounded hover:text-red-300" title="Delete">
                          <Trash2 size={10} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{total} total entries</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 rounded hover:bg-dark-50 disabled:opacity-30">
              <ChevronLeft size={14} />
            </button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="p-1 rounded hover:bg-dark-50 disabled:opacity-30">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Redirect Modal */}
      {redirectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-md mx-4 p-5">
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <ArrowRight size={16} className="text-copper" /> Add Redirect
            </h3>
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">From (404 URL)</label>
              <code className="block text-xs text-red-400 bg-dark rounded p-2 break-all">{redirectModal.url}</code>
            </div>
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1">Redirect To</label>
              <input
                value={redirectTo}
                onChange={(e) => setRedirectTo(e.target.value)}
                placeholder="https://..."
                autoFocus
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRedirectModal(null)} className="px-4 py-2 text-xs text-gray-400 hover:text-white">Cancel</button>
              <button onClick={handleResolve} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-copper text-dark rounded-lg font-medium hover:bg-copper/90">
                <Check size={14} /> Create Redirect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
